import { sortBy, uniq } from "lodash"

import {
  type StoryTimeRecorder,
  type StoryTimeRecording,
  type StoryTimeRecordingsByStudent,
} from "#customTypes/composedTypes/classSessionTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { invariant, type IsoDate } from "#customTypes/types"

export const buildStoryTimeStoragePath = (
  classId: string,
  sessionId: string,
  studentId: string,
  recordingId: string
): string => `storyTime/${classId}/${sessionId}/${studentId}/${recordingId}`

const nonEmptyId = (value: Uuid | null | undefined): string =>
  typeof value === "string" ? value.trim() : ""

/**
 * A teacher is a user id. A TA is a student id until TAs get user accounts,
 * and that user id may equal the student id. Keep every id we were given.
 */
export const buildStoryTimeRecorder = ({
  userId,
  studentId,
}: {
  userId?: Uuid | null
  studentId?: Uuid | null
}): StoryTimeRecorder => {
  const user = nonEmptyId(userId)
  const student = nonEmptyId(studentId)
  invariant(
    user !== "" || student !== "",
    "A storytime recording needs the teacher user id or the TA student id."
  )
  const recorder: StoryTimeRecorder = {}
  if (user !== "") recorder.userId = user
  if (student !== "") recorder.studentId = student
  return recorder
}

const assertMapKey = (value: string, label: string) => {
  invariant(
    value.length > 0 && !value.includes("."),
    `${label} is not a safe storytime map key`
  )
}

export const buildStoryTimeRecording = ({
  id,
  studentId,
  name,
  contentType,
  size,
  path,
  url,
  uploadedAt,
  recordedBy,
}: {
  id: string
  studentId: Uuid
  name: string
  contentType: string
  size: number
  path: string
  url: string
  uploadedAt: IsoDate
  recordedBy: StoryTimeRecorder
}): StoryTimeRecording => {
  assertMapKey(studentId, "Student id")
  assertMapKey(id, "Recording id")
  return {
    id,
    studentId,
    name,
    contentType,
    size,
    path,
    url,
    uploadedAt,
    recordedBy,
  }
}

/**
 * One field per recording. `updateDoc` merges dotted paths, so writing one
 * student's clip does not replace anyone else's. The id has no dots, so the
 * path is exactly three segments: field, student, recording.
 */
export const storyTimeRecordingsPatch = (
  recordings: StoryTimeRecording[]
): Record<string, StoryTimeRecording> => {
  const patch: Record<string, StoryTimeRecording> = {}
  for (const recording of recordings) {
    assertMapKey(recording.studentId, "Student id")
    assertMapKey(recording.id, "Recording id")
    patch[`storyTimeRecordings.${recording.studentId}.${recording.id}`] =
      recording
  }
  return patch
}

export const listStoryTimeRecordings = (
  recordingsByStudent: StoryTimeRecordingsByStudent | null | undefined
): StoryTimeRecording[] => {
  if (recordingsByStudent == null) return []
  const recordings: StoryTimeRecording[] = []
  for (const byRecordingId of Object.values(recordingsByStudent)) {
    if (byRecordingId == null) continue
    for (const recording of Object.values(byRecordingId)) {
      if (recording == null) continue
      recordings.push(recording)
    }
  }
  return sortBy(recordings, ["studentId", "uploadedAt", "id"])
}

type StoryTimeShareFields = {
  id: string
  isShared?: boolean
  timeCreated?: number
}

/** Missing or false is not for parents. Only an explicit true is shared. */
export const isStoryTimeShared = (
  storyTime: { isShared?: boolean } | null | undefined
): boolean => storyTime?.isShared === true

const storyTimeCreatedAt = (storyTime: StoryTimeShareFields): number =>
  typeof storyTime.timeCreated === "number" &&
  Number.isFinite(storyTime.timeCreated)
    ? storyTime.timeCreated
    : Number.NEGATIVE_INFINITY

/** Latest shared recording. A newer unshared clip does not win. */
export const latestSharedStoryTime = <T extends StoryTimeShareFields>(
  storyTimes: readonly T[]
): T | undefined => {
  let best: T | undefined
  for (const storyTime of storyTimes) {
    if (!isStoryTimeShared(storyTime)) continue
    if (!best) {
      best = storyTime
      continue
    }
    const created = storyTimeCreatedAt(storyTime)
    const bestCreated = storyTimeCreatedAt(best)
    if (
      created > bestCreated ||
      (created === bestCreated && storyTime.id > best.id)
    ) {
      best = storyTime
    }
  }
  return best
}

const GUARDIAN_EMAIL_SUFFIX = {
  FIRST: "-guardian1Email",
  SECOND: "-guardian2Email",
} as const

/**
 * Guardian addresses selected for this student. A checked student with no
 * contact leaf selected includes both guardians. Personal email is never
 * included. Phone-only selections send nothing.
 */
export const guardianEmailsForStoryTimeSend = (
  student: {
    id: string
    guardian1Email?: string
    guardian2Email?: string
  },
  checkedKeys: readonly string[]
): string[] => {
  const relevant = checkedKeys.filter(
    (key) => key === student.id || key.startsWith(`${student.id}-`)
  )
  if (relevant.length === 0) return []
  const contactKeys = relevant.filter(
    (key) => key.endsWith("Email") || key.endsWith("Phone")
  )
  const wants = (suffix: string): boolean =>
    contactKeys.length === 0 || relevant.some((key) => key.endsWith(suffix))
  const emails: string[] = []
  const add = (email: string | undefined) => {
    const trimmed = email?.trim() ?? ""
    if (trimmed === "" || emails.includes(trimmed)) return
    emails.push(trimmed)
  }
  if (wants(GUARDIAN_EMAIL_SUFFIX.FIRST)) add(student.guardian1Email)
  if (wants(GUARDIAN_EMAIL_SUFFIX.SECOND)) add(student.guardian2Email)
  return emails
}

export interface StoryTimeSendPlanItem {
  studentId: string
  emails: string[]
  storyTimeId: string
}

/** One newest shared recording per selected student who has a guardian address. */
type ShareableStoryTime = StoryTimeShareFields & {
  studentId: string
  url?: string
}

export const planSharedStoryTimeSends = (
  storyTimes: readonly ShareableStoryTime[],
  students: readonly {
    id: string
    guardian1Email?: string
    guardian2Email?: string
  }[],
  selectedStudentIds: readonly string[],
  checkedKeys: readonly string[]
): StoryTimeSendPlanItem[] => {
  const studentsById = new Map(students.map((student) => [student.id, student]))
  const plans: StoryTimeSendPlanItem[] = []
  for (const studentId of uniq(
    storyTimes.map((storyTime) => storyTime.studentId)
  )) {
    if (!selectedStudentIds.includes(studentId)) continue
    const student = studentsById.get(studentId)
    if (!student) continue
    const emails = guardianEmailsForStoryTimeSend(student, checkedKeys)
    const storyTime = latestSharedStoryTime(
      storyTimes.filter(
        (candidate) =>
          candidate.studentId === studentId &&
          typeof candidate.url === "string" &&
          candidate.url.trim() !== ""
      )
    )
    if (!storyTime || emails.length === 0) continue
    plans.push({ studentId, emails, storyTimeId: storyTime.id })
  }
  return plans
}

/**
 * One Mandrill call per student. `sendStudent` returns how many guardian
 * addresses were emailed, or null when that student had nothing to send.
 * The id is recorded only after the call succeeds, so a retry skips families
 * who already got the message and still tries the student who failed.
 */
// Mandrill caps a message at 25MB. Base64 grows files by 4/3, so the
// encoded total is checked before the send.
export const STORYTIME_EMAIL_BASE64_LIMIT = 24 * 1024 * 1024

export const estimateBase64Bytes = (fileSizeBytes: number): number =>
  Math.ceil(Math.max(0, fileSizeBytes) / 3) * 4

/** Encoded size of one student's email: that student's files plus the clip. */
export const storyTimeMessageBase64Bytes = (
  fileSizes: readonly number[],
  storyTimeBytes = 0
): number =>
  estimateBase64Bytes(storyTimeBytes) +
  fileSizes.reduce((sum, size) => sum + estimateBase64Bytes(size), 0)

export const storyTimeMessageOverLimit = (
  fileSizes: readonly number[],
  storyTimeBytes = 0
): boolean =>
  storyTimeMessageBase64Bytes(fileSizes, storyTimeBytes) >
  STORYTIME_EMAIL_BASE64_LIMIT

/** True when already-encoded attachment contents exceed one Mandrill message. */
export const encodedAttachmentsOverLimit = (
  encodedLengths: readonly number[]
): boolean =>
  encodedLengths.reduce((sum, length) => sum + length, 0) >
  STORYTIME_EMAIL_BASE64_LIMIT

export const emailStoryTimeStudentsOnce = async (
  studentIds: readonly string[],
  alreadyEmailed: Set<string>,
  sendStudent: (studentId: string) => Promise<number | null>
): Promise<number> => {
  let sentRecipients = 0
  for (const studentId of studentIds) {
    if (alreadyEmailed.has(studentId)) continue
    const count = await sendStudent(studentId)
    if (count == null) continue
    alreadyEmailed.add(studentId)
    sentRecipients += count
  }
  return sentRecipients
}

/** A failed send must say who already received the email. */
export const storyTimeSendFailureMessage = (
  alreadyEmailedNames: readonly string[],
  detail: string,
  recordingSaved: boolean
): string => {
  const names = alreadyEmailedNames
    .map((name) => name.trim())
    .filter((name) => name !== "")
  if (names.length === 0) {
    return recordingSaved
      ? `Recording saved on this session, but the email failed: ${detail}`
      : detail
  }
  const saved = recordingSaved ? " Recording saved on this session." : ""
  return `Already emailed ${names.join(", ")}. Send again to finish the rest. ${detail}${saved}`
}
